import { Center } from '@mantine/core'
import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Loading from '@/components/Loading'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useAuth } from '@/plugins/auth'

export interface ChildrenProps {
  children: React.ReactNode
}

const LoginRedirector: React.FC<ChildrenProps> = ({ children }) => {
  const navigate = useNavigate()
  const { address, userIdentity } = useAuth()
  const [firstLogin, setFirstLogin] = useLocalStorage<boolean>(`firstLogin-${address}`, true)
  const { pathname } = useLocation()
  // Open Hub on tab that prompt user to connect
  useEffect(() => {
    if (pathname.includes('/sign')) {
      const searchParams = new URLSearchParams(window.location.search)
      const fullPath = `${pathname}?${searchParams.toString()}`
      window.sessionStorage.setItem('oldRoute', fullPath)
    } else if (firstLogin && !userIdentity?.username) {
      navigate(`/freeusername`, {
        state: {
          pathname: '/profile',
        },
      })
    } else {
      setFirstLogin(false)
    }
    // Prevent navigation to /profile to avoid an infinite redirect loop to /profile/address
    if (!firstLogin && !pathname.includes('/profile')) {
      const oldRoute = window.sessionStorage.getItem('oldRoute')
      if (oldRoute) {
        navigate(oldRoute)
      }
    }
  }, [firstLogin, userIdentity, pathname, navigate, setFirstLogin])

  return firstLogin ? (
    <Center style={{ height: '100vh', width: '100vw' }}>
      <Loading />
    </Center>
  ) : (
    children
  )
}

// Component used to redirect user to the correct page after connected
const ConnectMiddleware: React.FC<ChildrenProps> = ({ children }) => {
  const { status, connect } = useAuth()
  const { pathname } = useLocation()
  const firstLoad = useRef<boolean>(false)

  useEffect(() => {
    if (pathname.includes('sign') && connect && !firstLoad.current) {
      firstLoad.current = true
      connect?.()
    }
  }, [pathname, connect])

  return status !== 'connected' ? <>{children}</> : <LoginRedirector>{children}</LoginRedirector>
}

export default ConnectMiddleware
